import { initContract } from '@ts-rest/core';
import { z } from 'zod';

const c = initContract();

const ErrorSchema = z.object({ message: z.string() });

const text = z.string().optional();
const amount = z.number().optional();
const flag = z.boolean().optional();

/**
 * One goods item of a batch as a flat row, with the stored risk analysis results. ai-platform's
 * risk analysis agent reads these rows; the field names are its vocabulary.
 */
export const AiPlatformBatchItemSchema = z.object({
  declarationId: z.string(),
  goodsShipmentItemId: z.string(),
  lrn: text,
  declarationStatus: text,
  originCountry: text,
  destinationCountry: text,
  intracomVat: text,
  itemNumber: amount,
  description: text,
  hsCode: text,
  hsChapter: text,
  hs6: text,
  netMass: amount,
  grossMass: amount,
  totalAmountInvoiced: amount,
  invoiceCurrency: text,
  totalDutiesAndTaxesAmount: amount,
  webUrl: text,
  sellerId: text,
  sellerVat: text,
  webCountryCode: text,
  refWebValueMethod: text,
  webDescription: text,
  webItemAmountResale: amount,
  webValue: amount,
  invoicedRate: amount,
  invoicedAmountPerKg: amount,
  webInvoicedRateOk: flag,
  validatedValueCheck: flag,
  webWeightOk: flag,
  vatSellerOk: flag,
  madbAmountPerKg: amount,
  madbLowestRate: amount,
  madbLowestOk: flag,
  madbSuggestedLowestOk: flag,
  hsCodeOk: flag,
  givenDistance: amount,
  suggestedHsCode: text,
  hsCodeFound: flag,
  taricDutyNotFound: flag,
  taricWithRestrictions: flag,
  taricRatioToCheck: flag,
  taricOriginException: flag,
  antiDumping: flag,
  validatedHSCodeCheck: flag,
  webMatchingDescription: flag,
  validatedDescriptionCheck: flag,
  anomaliesDetected: flag,
  invoicedRateOk: flag,
  referenceValue: text,
  additionalDuties: amount,
  additionalVat: amount,
  additionalDutiesAndTaxesAmount: amount,
  adjustedTotalDutiesAndTaxesAmount: amount,
});

export type AiPlatformBatchItem = z.infer<typeof AiPlatformBatchItemSchema>;

/** Read-only routes for ai-platform's services, authenticated with the shared `AI_PLATFORM_API_TOKEN`; no user can call them. */
export const aiPlatformContract = c.router({
  listBatchGoodsItems: {
    method: 'GET',
    path: '/ai-platform/batches/:batchId/goods-items',
    pathParams: z.object({ batchId: z.string() }),
    responses: {
      200: z.object({ batchId: z.string(), items: z.array(AiPlatformBatchItemSchema) }),
      401: ErrorSchema,
      403: ErrorSchema,
    },
  },
});
